import type { ReactNode } from 'react'
import type { ApiFailure } from '../../api/types'
import { isApiError } from '../../api/types'
import { Button } from '../Button'
import { StateLayout } from './StateLayout'
import { AlertIcon, RetryIcon } from '../icons'
import type { StateScale } from './EmptyState'

export interface ProblemStateProps {
  readonly error: unknown
  readonly onRetry?: (() => void) | undefined
  readonly title?: string | undefined
  readonly scale: StateScale
  readonly className?: string | undefined
  readonly action?: ReactNode
}

function toFailure(error: unknown): ApiFailure | null {
  if (isApiError(error)) return error.failure
  if (
    typeof error === 'object' &&
    error !== null &&
    'kind' in error &&
    'retryable' in error &&
    'message' in error
  ) {
    return error as ApiFailure
  }
  return null
}

const headings: Record<ApiFailure['kind'], string> = {
  Unauthenticated: 'Your session has ended',
  Forbidden: 'You do not have access to this',
  NotFound: 'This no longer exists',
  Conflict: 'This conflicts with the current state',
  StaleWrite: 'Someone else saved first',
  Validation: 'Some values were rejected',
  Network: 'Argus could not be reached',
  Unexpected: 'The request failed',
}

export function ProblemState({
  error,
  onRetry,
  title,
  scale,
  className,
  action,
}: ProblemStateProps) {
  const failure = toFailure(error)
  const heading = title ?? (failure ? headings[failure.kind] : 'The request failed')
  const detail =
    failure?.message ??
    (error instanceof Error ? error.message : 'The request did not complete.')
  const canRetry = Boolean(onRetry) && (failure?.retryable ?? false)

  return (
    <div
      role="alert"
      data-testid="problem-state"
      data-kind={failure?.kind ?? 'Unexpected'}
      data-scale={scale}
    >
      <StateLayout
        scale={scale}
        className={className}
        illustration={
          scale !== 'page'
            ? false
            : failure?.kind === 'NotFound'
              ? 'cactus'
              : !failure || failure.kind === 'Network' || failure.kind === 'Unexpected'
                ? 'connection'
                : false
        }
      >
        <div className="flex items-center gap-2 text-destructive">
          {scale === 'embedded' ? <AlertIcon /> : null}
          <h2
            className={
              scale === 'embedded'
                ? 'text-sm font-medium text-foreground'
                : 'font-heading text-xl font-semibold text-foreground'
            }
          >
            {heading}
          </h2>
        </div>

        <p className="text-sm text-muted-foreground">{detail}</p>

        {failure?.kind === 'Validation' && failure.issues.length > 0 ? (
          <ul className="flex list-none flex-col gap-1 border-l-2 border-l-border pl-3">
            {failure.issues.map((issue) => (
              <li key={`${issue.pointer}:${issue.code}`} className="text-sm text-foreground">
                <span className="text-muted-foreground">{issue.pointer || 'request'}</span> —{' '}
                {issue.message}
              </li>
            ))}
          </ul>
        ) : null}

        {canRetry && onRetry ? (
          <Button variant="secondary" size="sm" onClick={onRetry}>
            <RetryIcon />
            Try again
          </Button>
        ) : null}
        {action}

        {failure?.requestId ? (
          <p className="text-xs text-muted-foreground">
            Request id{' '}
            <span className="font-mono text-foreground select-all">{failure.requestId}</span>
          </p>
        ) : null}
      </StateLayout>
    </div>
  )
}
